import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

const STORAGE_KEY = 'gielda4x4_comparison_offers'
const MAX_COMPARISON_OFFERS = 4

export const useComparisonStore = defineStore('comparison', () => {
  const comparisonOffers = ref([])
  const loading = ref(false)

  // Load comparison offers from localStorage on store initialization
  const loadFromStorage = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        comparisonOffers.value = JSON.parse(stored)
      }
    } catch (error) {
      console.error('Error loading comparison offers from storage:', error)
      comparisonOffers.value = []
    }
  }

  // Save comparison offers to localStorage
  const saveToStorage = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(comparisonOffers.value))
    } catch (error) {
      console.error('Error saving comparison offers to storage:', error)
    }
  }

  // Computed properties
  const comparisonCount = computed(() => comparisonOffers.value.length)
  const hasComparisonOffers = computed(() => comparisonOffers.value.length > 0)
  const canAddMore = computed(() => comparisonOffers.value.length < MAX_COMPARISON_OFFERS)
  const isFull = computed(() => comparisonOffers.value.length >= MAX_COMPARISON_OFFERS)

  // Check if an offer is in comparison
  const isInComparison = (offerId) => {
    return comparisonOffers.value.some(offer => offer.id === offerId)
  }

  // Add offer to comparison
  const addToComparison = (offer) => {
    if (!offer || !offer.id) {
      console.error('Invalid offer data')
      return false
    }

    if (isInComparison(offer.id)) {
      return false
    }

    if (comparisonOffers.value.length >= MAX_COMPARISON_OFFERS) {
      return false
    }

    // Add detailed offer data for comparison
    const comparisonOffer = {
      id: offer.id,
      title: offer.title,
      price: offer.price,
      currency: offer.currency,
      productionYear: offer.productionYear,
      mileage: offer.mileage,
      mileageUnit: offer.mileageUnit,
      brand: offer.brand,
      model: offer.model,
      carSpecification: offer.carSpecification,
      location: offer.location,
      media: offer.media,
      slug: offer.slug,
      createdAt: offer.createdAt,
      addedToComparisonAt: new Date().toISOString(),
      // Add engine details
      engineCapacity: offer.engineCapacity,
      enginePower: offer.enginePower,
      // Add attributes - store the raw API response
      offerAttributes: offer.offerAttributes || []
    }

    comparisonOffers.value.push(comparisonOffer)
    saveToStorage()
    return true
  }

  // Remove offer from comparison
  const removeFromComparison = (offerId) => {
    const index = comparisonOffers.value.findIndex(offer => offer.id === offerId)
    if (index !== -1) {
      comparisonOffers.value.splice(index, 1)
      saveToStorage()
      return true
    }
    return false
  }

  // Clear all comparison offers
  const clearComparison = () => {
    comparisonOffers.value = []
    saveToStorage()
  }

  // Get comparison offers with full data (for comparison page)
  const getComparisonOffers = () => {
    return comparisonOffers.value
  }

  // Initialize store
  loadFromStorage()

  return {
    // State
    comparisonOffers,
    loading,

    // Computed
    comparisonCount,
    hasComparisonOffers,
    canAddMore,
    isFull,

    // Methods
    isInComparison,
    addToComparison,
    removeFromComparison,
    clearComparison,
    getComparisonOffers,
    loadFromStorage,
    saveToStorage
  }
}) 